import { Skeleton } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { Helmet } from "react-helmet-async";

import { type AdminRevenueSnapshot, useAdminRevenue } from "@/admin/services";

import { AdminDataTable } from "../../components/ui/data-table";
import { KPICard } from "../_components/kpi-card";
import { PageHeader } from "../_components/page-header";

type Payment = AdminRevenueSnapshot["recentPayments"][number];

const formatMoney = (cents: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(
    cents / 100,
  );

export const AdminRevenuePage = () => {
  const { data, isLoading } = useAdminRevenue();

  const columns: ColumnDef<Payment>[] = [
    {
      accessorKey: "customerEmail",
      header: "Customer",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="text-[13px] font-medium text-foreground">
            {row.original.customerName || row.original.customerEmail || "—"}
          </span>
          {row.original.customerName && row.original.customerEmail && (
            <span className="text-[12px] text-muted-foreground">{row.original.customerEmail}</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row }) => (
        <span className="font-medium tabular-nums text-foreground">
          {formatMoney(row.original.amount, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]",
            row.original.status === "succeeded"
              ? "bg-success-soft text-success-foreground"
              : "bg-surface text-muted-foreground",
          )}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => (
        <span className="text-[12px] text-muted-foreground">{row.original.description ?? "—"}</span>
      ),
    },
    {
      accessorKey: "createdAt",
      header: "When",
      cell: ({ row }) => (
        <span className="text-[12px] tabular-nums text-muted-foreground">
          {new Date(row.original.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      id: "stripe",
      header: "",
      cell: ({ row }) => (
        <a
          href={row.original.stripeDashboardUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
        >
          Stripe
          <ExternalLink className="size-3" />
        </a>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Revenue · Admin</title>
      </Helmet>

      <PageHeader
        title="Revenue"
        description="Skimmable view. Heavy analysis lives in the embedded Grafana dashboard."
        trail={["Admin"]}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <KPICard key={i} label="Loading" value="—" loading />
            ))
          : (data?.kpis ?? []).map((kpi) => (
              <KPICard
                key={kpi.label}
                label={kpi.label}
                value={
                  kpi.format === "currency"
                    ? formatMoney(kpi.value, kpi.currency ?? "usd")
                    : kpi.value.toLocaleString()
                }
                wowDelta={kpi.wowDelta}
              />
            ))}
      </div>

      <section className="mt-8">
        <header className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
              Recent activity
            </p>
            <h2 className="text-[15px] font-semibold text-foreground">Last 20 payments</h2>
          </div>
          {data?.updatedAt && (
            <p className="text-[11px] text-muted-foreground tabular-nums">
              Last refreshed {new Date(data.updatedAt).toLocaleTimeString()}
            </p>
          )}
        </header>
        {isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <AdminDataTable columns={columns} data={data?.recentPayments ?? []} pageSize={20} />
        )}
      </section>
    </>
  );
};

export default AdminRevenuePage;
